import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  alpha,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Description as TemplateIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LibraryBooks as MasterIcon,
  CloudUpload as SubmitIcon,
  Sync as VerifyIcon,
  PauseCircle as PauseIcon,
  PlayCircle as ResumeIcon,
  Visibility as ViewIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  PauseCircleOutline as PausedIcon,
  School as SchoolIcon,
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import apiService from "../../../services/apiService";
import { crmColors } from "../../../theme/colors";

const WA = crmColors.whatsapp;
const WA_DARK = "#1DA851";
const HEADER_BG = alpha(WA, 0.08);

const STATUS_COLORS = {
  approved: "success",
  pending: "warning",
  rejected: "error",
  paused: "default",
};

const CATEGORIES = [
  { value: "UTILITY", label: "Utility" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Authentication" },
];

const emptyForm = {
  school_code: "",
  name: "",
  category: "UTILITY",
  language: "en",
  group_name: "",
  header_text: "",
  body_text: "",
  footer_text: "",
  submit_to_meta: false,
};

const KPI_ICON_BG = ["#A26C02", "#5e35b1", "#00897b", "#E74900", "#455a64"];

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
};

const n = (v) => Number(v) || 0;

const SCHOOLS_PAGE_SIZE = 100;

/** EduOKWeb adm-kpi__metric */
const KpiTile = memo(function KpiTile({
  title,
  value,
  icon,
  index = 0,
  hint,
  onClick,
}) {
  const iconBg = KPI_ICON_BG[index % KPI_ICON_BG.length];
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className="adm-kpi__metric"
      sx={{
        appearance: "none",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.5,
        minHeight: 88,
        borderRadius: "12px",
        border: "2px solid rgba(3, 169, 244, 0.35)",
        background: "linear-gradient(165deg, #ffffff 0%, #fff6e0 100%)",
        boxShadow: "0 6px 16px rgba(2, 136, 209, 0.07)",
        color: "inherit",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "inherit",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-2px)",
              boxShadow: "0 10px 22px rgba(2, 136, 209, 0.12)",
            }
          : undefined,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: iconBg,
          color: "#fff",
          boxShadow: `0 4px 10px ${alpha(iconBg, 0.32)}`,
          "& .MuiSvgIcon-root": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
        <Typography
          component="small"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: "#78909c",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          component="strong"
          sx={{
            fontSize: { xs: 18, md: 20 },
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1.15,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
        {hint ? (
          <Typography
            component="em"
            sx={{
              fontStyle: "normal",
              fontSize: 11,
              fontWeight: 600,
              color: "#64748b",
              lineHeight: 1.25,
            }}
          >
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
});

const sectionHeadSx = {
  px: 2,
  py: 1.35,
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  borderBottom: `1px solid ${alpha(WA, 0.12)}`,
  bgcolor: HEADER_BG,
};

const tableHeadCellSx = {
  fontWeight: 800,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#475569",
  bgcolor: alpha(WA, 0.055),
  borderBottom: `1.5px solid ${alpha(WA, 0.18)}`,
  py: 1.35,
  px: 2,
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  py: 1.35,
  px: 2,
  borderBottom: `1px solid ${alpha("#94a3b8", 0.14)}`,
  verticalAlign: "middle",
};

const tableRowSx = {
  transition: "background-color 0.14s ease",
  "&:nth-of-type(even)": { bgcolor: alpha("#f8fafc", 0.9) },
  "&:hover": { bgcolor: `${alpha(WA, 0.07)} !important` },
  "&.Mui-selected": {
    bgcolor: `${alpha(WA, 0.12)} !important`,
    "&:hover": { bgcolor: `${alpha(WA, 0.16)} !important` },
  },
};

const tablePaginationSx = {
  borderTop: `1px solid ${alpha(WA, 0.12)}`,
  bgcolor: alpha(WA, 0.03),
  ".MuiTablePagination-toolbar": { minHeight: 48, px: 1.5 },
  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#64748b",
  },
};

const CountPill = memo(function CountPill({ value, tone = "neutral" }) {
  const tones = {
    success: { bg: alpha("#2e7d32", 0.12), color: "#1b5e20", border: alpha("#2e7d32", 0.22) },
    warning: { bg: alpha("#ed6c02", 0.12), color: "#e65100", border: alpha("#ed6c02", 0.22) },
    neutral: { bg: alpha("#64748b", 0.1), color: "#334155", border: alpha("#64748b", 0.18) },
    wa: { bg: alpha(WA, 0.14), color: WA_DARK, border: alpha(WA, 0.28) },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 44,
        px: 1.1,
        py: 0.35,
        borderRadius: "999px",
        bgcolor: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        fontWeight: 800,
        fontSize: "0.78rem",
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.2,
      }}
    >
      {Number(value) || 0}
    </Box>
  );
});

/**
 * Manage school WhatsApp templates - create, submit Meta, verify, pause.
 * Layout: KPIs -> filters -> tables.
 */
const WhatsAppTemplates = memo(function WhatsAppTemplates({
  view = "school",
  refreshSignal = 0,
  openCreateSignal = 0,
}) {
  const isMasterView = view === "master";
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [master, setMaster] = useState([]);
  const [summary, setSummary] = useState({});
  const [schoolStats, setSchoolStats] = useState([]);
  const [schoolPage, setSchoolPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [schoolOptions, setSchoolOptions] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [manageSchool, setManageSchool] = useState(null);
  const [manageTemplates, setManageTemplates] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageStatus, setManageStatus] = useState("all");
  const [verifyReportOpen, setVerifyReportOpen] = useState(false);
  const [verifyReport, setVerifyReport] = useState(null);

  const loadSchoolOptions = useCallback(async (keyword = "") => {
    setSchoolsLoading(true);
    if (keyword) {
      const result = await apiService.searchSchools(keyword, "school_name");
      const list = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.schools)
          ? result.data.schools
          : [];
      setSchoolOptions(
        list.map((s) => ({
          school_code: s.school_code,
          school_name: s.school_name || s.name || s.school_code,
        }))
      );
    } else {
      const result = await apiService.getWhatsAppAdminSchools({
        status: "connected",
        limit: 50,
        page: 1,
      });
      const list = Array.isArray(result.data) ? result.data : [];
      setSchoolOptions(
        list.map((s) => ({
          school_code: s.school_code,
          school_name: s.school_name || s.name || s.school_code,
        }))
      );
    }
    setSchoolsLoading(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getWhatsAppAdminTemplates({
      search,
      status,
      school_code: "",
      limit: 100,
      offset: 0,
    });
    if (!result.success) {
      toast.error(result.error || "Failed to load template management");
      setLoading(false);
      return;
    }
    const data = result.data || {};
    const nextItems = Array.isArray(data.items) ? data.items : [];
    setItems(nextItems);
    setMaster(Array.isArray(data.master) ? data.master : []);
    setSummary(data.summary || {});
    setTotal(Number(data.total) || 0);

    let nextSchools = Array.isArray(data.schools) ? data.schools : [];
    // Fallback: derive school rows from template items if API schools list is empty
    if (!nextSchools.length && nextItems.length) {
      const map = new Map();
      nextItems.forEach((row) => {
        const code = String(row.school_code || "").trim();
        if (!code) return;
        if (!map.has(code)) {
          map.set(code, {
            school_code: code,
            school_name: row.school_name || code,
            active_count: 0,
            pending_count: 0,
            total_count: 0,
          });
        }
        const entry = map.get(code);
        entry.total_count += 1;
        const st = String(row.status || "").toLowerCase();
        if (st === "approved") entry.active_count += 1;
        if (st === "pending") entry.pending_count += 1;
      });
      nextSchools = Array.from(map.values()).sort(
        (a, b) => b.pending_count - a.pending_count || b.active_count - a.active_count
      );
    }
    setSchoolStats(nextSchools);
    setSchoolPage(0);
    setLoading(false);
  }, [search, status]);

  const loadManageTemplates = useCallback(async (schoolCode, statusFilter = "all") => {
    const code = String(schoolCode || "").trim();
    if (!code) {
      setManageTemplates([]);
      return;
    }
    setManageLoading(true);
    const result = await apiService.getWhatsAppAdminTemplates({
      search: "",
      status: statusFilter || "all",
      school_code: code,
      limit: 200,
      offset: 0,
    });
    setManageLoading(false);
    if (!result.success) {
      toast.error(result.error || "Failed to load school templates");
      setManageTemplates([]);
      return;
    }
    const codeKey = code.toLowerCase();
    const items = (Array.isArray(result.data?.items) ? result.data.items : []).filter(
      (row) => String(row.school_code || "").trim().toLowerCase() === codeKey
    );
    setManageTemplates(items);
  }, []);

  const openSchoolManage = useCallback(
    (school) => {
      if (!school?.school_code) return;
      const next = {
        school_code: String(school.school_code).trim(),
        school_name: school.school_name || school.school_code,
      };
      setManageSchool(next);
      setManageStatus("all");
      setManageTemplates([]);
      loadManageTemplates(next.school_code, "all");
    },
    [loadManageTemplates]
  );

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    loadSchoolOptions();
  }, [loadSchoolOptions]);

  const openCreate = (prefillSchool = null) => {
    const school = prefillSchool || manageSchool;
    setForm({
      ...emptyForm,
      school_code: school?.school_code || "",
    });
    setSelectedSchool(
      school?.school_code
        ? { school_code: school.school_code, school_name: school.school_name || school.school_code }
        : null
    );
    setDialogOpen(true);
    loadSchoolOptions();
  };

  useEffect(() => {
    if (openCreateSignal > 0) openCreate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open on signal only
  }, [openCreateSignal]);

  const handleSave = async () => {
    const schoolCode = selectedSchool?.school_code || form.school_code;
    if (!schoolCode) {
      toast.error("Select a school");
      return;
    }
    if (!form.name.trim() || !form.body_text.trim()) {
      toast.error("Template name and body are required");
      return;
    }

    setSaving(true);
    const payload = {
      action: form.submit_to_meta ? "create_meta" : "save_local",
      school_code: schoolCode,
      name: form.name.trim(),
      category: form.category,
      language: form.language.trim() || "en",
      group_name: form.group_name.trim(),
      header_text: form.header_text.trim(),
      body_text: form.body_text.trim(),
      footer_text: form.footer_text.trim(),
    };
    const result = await apiService.manageWhatsAppSchoolTemplate(payload);
    setSaving(false);
    if (!result.success) {
      toast.error(result.error || "Failed to save template");
      return;
    }
    toast.success(form.submit_to_meta ? "Template submitted to Meta" : "Template saved as pending");
    setDialogOpen(false);
    load();
    if (manageSchool?.school_code) {
      loadManageTemplates(manageSchool.school_code, manageStatus);
    }
  };

  const runAction = async (row, action, extra = {}) => {
    if (!row?.id || !row?.school_code) return;
    setActionLoadingId(`${action}-${row.id}`);
    const payload = {
      action,
      school_code: String(row.school_code).trim(),
      template_id: Number(row.id),
      ...extra,
    };
    if (action === "verify_meta") {
      payload.apply_sync = 1;
    }
    const result = await apiService.manageWhatsAppSchoolTemplate(payload);
    setActionLoadingId(null);
    if (!result.success) {
      const detail = result.error || result.message || "Action failed";
      toast.error(detail);
      // Still show partial report when Meta fetch worked but sync failed
      if (result.data?.report?.rows) {
        setVerifyReport({
          ...(result.data || {}),
          template_id: row.id,
          template_name: row.name,
          school_code: row.school_code,
          error: detail,
        });
        setVerifyReportOpen(true);
      }
      return;
    }
    if (action === "verify_meta") {
      const data = result.data || {};
      const msg =
        data.message ||
        result.message ||
        (data.matched
          ? "Meta and DB match"
          : data.synced
            ? "Synced from Meta"
            : "Verified from Meta");
      toast.success(msg);
      setVerifyReport({
        ...data,
        template_id: row.id,
        template_name: row.name,
        school_code: row.school_code,
      });
      setVerifyReportOpen(true);
    } else {
      const messages = {
        submit_meta: "Submitted to Meta",
        update_status: `Status set to ${extra.status || "updated"}`,
        delete: "Template removed",
      };
      toast.success(result.message || messages[action] || "Done");
      // Refresh verify dialog after status update from report
      if (
        action === "update_status" &&
        verifyReportOpen &&
        verifyReport?.template_id &&
        Number(verifyReport.template_id) === Number(row.id)
      ) {
        const tpl = result.data?.template || null;
        setVerifyReport((prev) => {
          if (!prev) return prev;
          const nextStatus = String(extra.status || tpl?.status || "").toLowerCase();
          const rows = Array.isArray(prev.report?.rows)
            ? prev.report.rows.map((r) =>
                r.field === "status"
                  ? {
                      ...r,
                      eduok: nextStatus || r.eduok,
                      match:
                        String(r.meta || "").toLowerCase() === nextStatus ||
                        String(r.meta || "").toUpperCase() ===
                          String(extra.status || "").toUpperCase(),
                    }
                  : r
              )
            : prev.report?.rows;
          return {
            ...prev,
            needs_status_update: false,
            template: tpl || prev.template,
            message: `EduOK status updated to ${extra.status || "approved"}`,
            report: prev.report ? { ...prev.report, rows } : prev.report,
          };
        });
      }
    }
    load();
    if (manageSchool?.school_code) {
      loadManageTemplates(manageSchool.school_code, manageStatus);
    }
    if (viewOpen && viewRow?.id === row.id && result.data?.template) {
      setViewRow(result.data.template);
    }
  };

  const masterSummary = React.useMemo(() => {
    const out = { total: master.length, approved: 0, pending: 0, rejected: 0, paused: 0 };
    master.forEach((row) => {
      const st = String(row.status || "").toLowerCase();
      if (st === "approved") out.approved += 1;
      else if (st === "pending") out.pending += 1;
      else if (st === "rejected") out.rejected += 1;
      else if (st === "paused") out.paused += 1;
    });
    return out;
  }, [master]);

  const kpiSummary = isMasterView ? masterSummary : summary;
  const kpiTotal = isMasterView ? master.length : n(summary.total ?? total);

  const filteredMaster = React.useMemo(() => {
    if (!isMasterView) return master;
    const q = searchInput.trim().toLowerCase();
    return master.filter((row) => {
      const st = String(row.status || "").toLowerCase();
      if (status !== "all" && st !== status) return false;
      if (!q) return true;
      return (
        String(row.name || "").toLowerCase().includes(q) ||
        String(row.group_name || "").toLowerCase().includes(q) ||
        String(row.category || "").toLowerCase().includes(q)
      );
    });
  }, [isMasterView, master, searchInput, status]);

  const pagedSchools = React.useMemo(() => {
    const start = schoolPage * SCHOOLS_PAGE_SIZE;
    return schoolStats.slice(start, start + SCHOOLS_PAGE_SIZE);
  }, [schoolStats, schoolPage]);

  const filteredManageTemplates = React.useMemo(() => {
    const codeKey = String(manageSchool?.school_code || "")
      .trim()
      .toLowerCase();
    if (!codeKey) return [];
    return manageTemplates.filter((row) => {
      if (String(row.school_code || "").trim().toLowerCase() !== codeKey) return false;
      if (manageStatus === "all") return true;
      return String(row.status || "").toLowerCase() === manageStatus;
    });
  }, [manageTemplates, manageStatus, manageSchool]);

  const verifyNeedsStatusUpdate = React.useMemo(() => {
    if (!verifyReport) return false;
    if (verifyReport.needs_status_update) return true;
    const metaStatus = String(verifyReport.meta?.status || "").toUpperCase();
    const eduokStatus = String(
      verifyReport.template?.status ||
        verifyReport.report?.rows?.find((r) => r.field === "status")?.eduok ||
        ""
    ).toLowerCase();
    return metaStatus === "APPROVED" && eduokStatus === "pending";
  }, [verifyReport]);

  const handleUpdateStatusFromVerify = async () => {
    if (!verifyReport?.template_id || !verifyReport?.school_code) return;
    await runAction(
      {
        id: verifyReport.template_id,
        school_code: verifyReport.school_code,
        name: verifyReport.template_name,
      },
      "update_status",
      { status: "approved" }
    );
  };

  return (
    <Stack spacing={2.5}>
      {/* Primary KPIs */}
      <Box
        className="adm-kpi__metrics adm-kpi__metrics--top"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(5, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <KpiTile
          index={0}
          title="Total templates"
          value={kpiTotal}
          hint={
            isMasterView
              ? "Master catalog"
              : `${schoolStats.length} schools`
          }
          icon={<TemplateIcon />}
          onClick={() => setStatus("all")}
        />
        <KpiTile
          index={1}
          title="Approved"
          value={n(kpiSummary.approved)}
          hint="Live on Meta"
          icon={<ApprovedIcon />}
          onClick={() => setStatus("approved")}
        />
        <KpiTile
          index={2}
          title="Pending"
          value={n(kpiSummary.pending)}
          hint="Awaiting Meta review"
          icon={<PendingIcon />}
          onClick={() => setStatus("pending")}
        />
        <KpiTile
          index={3}
          title="Rejected"
          value={n(kpiSummary.rejected)}
          hint="Needs fix / resubmit"
          icon={<RejectedIcon />}
          onClick={() => setStatus("rejected")}
        />
        <KpiTile
          index={4}
          title="Paused"
          value={n(kpiSummary.paused)}
          hint="Temporarily disabled"
          icon={<PausedIcon />}
          onClick={() => setStatus("paused")}
        />
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          borderRadius: "12px",
          border: `1px solid ${alpha("#94a3b8", 0.4)}`,
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={1}
          sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
        >
          <Typography
            sx={{
              m: 0,
              fontSize: "0.875rem",
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.01em",
            }}
          >
            Overview & filters
          </Typography>
          <Typography sx={{ m: 0, fontSize: "0.7rem", fontWeight: 600, color: "#94a3b8" }}>
            {isMasterView
              ? "Search and filter master templates · tap a KPI to set status"
              : "Search and filter school templates · tap a KPI to set status"}
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
          <TextField
            size="small"
            placeholder={
              isMasterView
                ? "Search master template, group…"
                : "Search template, school, group…"
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 180 }}
          />
          {!isMasterView && (
            <Autocomplete
              size="small"
              options={schoolOptions}
              loading={schoolsLoading}
              value={null}
              getOptionLabel={(opt) =>
                opt?.school_name ? `${opt.school_name} (${opt.school_code})` : opt?.school_code || ""
              }
              isOptionEqualToValue={(a, b) => a?.school_code === b?.school_code}
              onInputChange={(_, value, reason) => {
                if (reason === "input") loadSchoolOptions(value);
              }}
              onChange={(_, value) => {
                if (value?.school_code) openSchoolManage(value);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Open school" placeholder="Search school…" />
              )}
              sx={{ minWidth: 220 }}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} sx={{ color: crmColors.primary.main }} />
        </Box>
      ) : isMasterView ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${alpha(WA, 0.16)}`,
            bgcolor: "#fff",
            overflow: "hidden",
            boxShadow: `0 8px 24px -18px ${alpha(crmColors.primary.dark, 0.35)}`,
          }}
        >
          <Box sx={{ ...sectionHeadSx, bgcolor: alpha(crmColors.primary.dark, 0.06) }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(crmColors.primary.dark, 0.14),
                color: crmColors.primary.dark,
                flexShrink: 0,
              }}
            >
              <MasterIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "#0f172a", flex: 1 }}>
              Template Master ({filteredMaster.length})
            </Typography>
            <Tooltip title="Refresh list">
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {!filteredMaster.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              No master templates found
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    {["Template", "Category", "Language", "Status", "Group"].map((h) => (
                      <TableCell key={h} sx={tableHeadCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMaster.map((row) => (
                    <TableRow key={`master-${row.id}`} hover sx={tableRowSx}>
                      <TableCell sx={tableBodyCellSx}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "10px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              bgcolor: alpha(crmColors.primary.dark, 0.12),
                              color: crmColors.primary.dark,
                              fontWeight: 800,
                              fontSize: "0.75rem",
                            }}
                          >
                            {String(row.name || "?").slice(0, 2).toUpperCase()}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ color: "#0f172a", lineHeight: 1.25 }}
                          >
                            {row.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, textTransform: "capitalize" }}>
                        <Chip
                          size="small"
                          label={row.category || "—"}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            bgcolor: alpha("#5e35b1", 0.1),
                            color: "#4527a0",
                            border: `1px solid ${alpha("#5e35b1", 0.18)}`,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Typography
                          variant="body2"
                          fontWeight={650}
                          sx={{ color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}
                        >
                          {row.language || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Chip
                          size="small"
                          label={row.status || "—"}
                          color={STATUS_COLORS[String(row.status || "").toLowerCase()] || "default"}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {row.group_name || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "14px",
              border: `1px solid ${alpha(WA, 0.16)}`,
              bgcolor: "#fff",
              overflow: "hidden",
              boxShadow: `0 8px 24px -18px ${alpha(WA, 0.45)}`,
            }}
          >
            <Box sx={sectionHeadSx}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(WA, 0.16),
                  color: WA_DARK,
                  flexShrink: 0,
                }}
              >
                <SchoolIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "#0f172a" }}>
                  Schools ({schoolStats.length})
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 600 }}>
                  Click a school to view and manage templates
                </Typography>
              </Box>
            </Box>
            {!schoolStats.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                {loading
                  ? "Loading schools…"
                  : n(summary.total ?? total) > 0
                    ? "Could not load school breakdown — try Refresh"
                    : "No WhatsApp schools / templates found yet"}
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 560 }}>
                    <TableHead>
                      <TableRow>
                        {["School", "Active", "Pending", "Total"].map((h) => (
                          <TableCell
                            key={h}
                            sx={tableHeadCellSx}
                            align={h === "School" ? "left" : "center"}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedSchools.map((s) => {
                        const selected = manageSchool?.school_code === s.school_code;
                        const initial = String(s.school_name || s.school_code || "?")
                          .trim()
                          .charAt(0)
                          .toUpperCase();
                        return (
                          <TableRow
                            key={s.school_code}
                            hover
                            selected={selected}
                            onClick={() => openSchoolManage(s)}
                            sx={{ ...tableRowSx, cursor: "pointer" }}
                          >
                            <TableCell sx={tableBodyCellSx}>
                              <Stack direction="row" spacing={1.35} alignItems="center">
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "11px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    bgcolor: selected ? alpha(WA, 0.22) : alpha(WA, 0.12),
                                    color: WA_DARK,
                                    fontWeight: 800,
                                    fontSize: "0.85rem",
                                    border: `1px solid ${alpha(WA, selected ? 0.35 : 0.2)}`,
                                  }}
                                >
                                  {initial}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    noWrap
                                    sx={{ color: "#0f172a", lineHeight: 1.25 }}
                                  >
                                    {s.school_name || s.school_code}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#64748b",
                                      fontWeight: 650,
                                      letterSpacing: "0.02em",
                                    }}
                                  >
                                    {s.school_code}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="center" sx={tableBodyCellSx}>
                              <CountPill value={s.active_count} tone="success" />
                            </TableCell>
                            <TableCell align="center" sx={tableBodyCellSx}>
                              <CountPill value={s.pending_count} tone="warning" />
                            </TableCell>
                            <TableCell align="center" sx={tableBodyCellSx}>
                              <CountPill value={s.total_count} tone="wa" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={schoolStats.length}
                  page={schoolPage}
                  onPageChange={(_, p) => setSchoolPage(p)}
                  rowsPerPage={SCHOOLS_PAGE_SIZE}
                  rowsPerPageOptions={[SCHOOLS_PAGE_SIZE]}
                  labelRowsPerPage="Schools per page"
                  sx={tablePaginationSx}
                />
              </>
            )}
          </Paper>

        </>
      )}

      <Dialog
        open={!!manageSchool}
        onClose={() => {
          setManageSchool(null);
          setManageTemplates([]);
          setManageStatus("all");
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: alpha(WA, 0.08),
            borderBottom: `1px solid ${alpha(WA, 0.14)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "11px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(WA, 0.18),
                color: WA_DARK,
                flexShrink: 0,
              }}
            >
              <SchoolIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#0f172a", lineHeight: 1.25 }}>
                {manageSchool?.school_name || "School templates"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 650, color: "#64748b" }}>
                {manageSchool?.school_code} · manage templates
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  disabled={manageLoading || !manageSchool?.school_code}
                  onClick={() => loadManageTemplates(manageSchool.school_code, manageStatus)}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => openCreate(manageSchool)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: WA,
                "&:hover": { bgcolor: WA_DARK },
              }}
            >
              Add template
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setManageSchool(null);
                setManageTemplates([]);
                setManageStatus("all");
              }}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 0, py: 0, bgcolor: "#fafbfc" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
            sx={{ px: 2.25, py: 1.5, bgcolor: "#fff", borderBottom: `1px solid ${alpha("#94a3b8", 0.16)}` }}
          >
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#64748b", flex: 1 }}>
              {manageLoading
                ? "Loading templates…"
                : `${filteredManageTemplates.length} template${filteredManageTemplates.length === 1 ? "" : "s"}`}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={manageStatus}
                onChange={(e) => {
                  const next = e.target.value;
                  setManageStatus(next);
                  if (manageSchool?.school_code) {
                    loadManageTemplates(manageSchool.school_code, next);
                  }
                }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {manageLoading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={30} sx={{ color: WA }} />
            </Box>
          ) : !filteredManageTemplates.length ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 5, textAlign: "center", fontWeight: 600 }}
            >
              No templates for this school yet. Click Add template to create one.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    {["Template", "Category", "Language", "Status", "Updated", "Actions"].map((h) => (
                      <TableCell
                        key={h}
                        sx={tableHeadCellSx}
                        align={h === "Actions" ? "right" : "left"}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredManageTemplates.map((row) => {
                    const st = String(row.status || "").toLowerCase();
                    const busy = (action) => actionLoadingId === `${action}-${row.id}`;
                    return (
                      <TableRow key={row.id} hover sx={tableRowSx}>
                        <TableCell sx={tableBodyCellSx}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: "#0f172a" }}>
                            {row.name}
                          </Typography>
                          {row.group_name ? (
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {row.group_name}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx, textTransform: "capitalize" }}>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {row.category || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Typography
                            variant="body2"
                            fontWeight={650}
                            sx={{ textTransform: "uppercase", letterSpacing: "0.04em", color: "#475569" }}
                          >
                            {row.language || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Chip
                            size="small"
                            label={row.status || "—"}
                            color={STATUS_COLORS[st] || "default"}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                            {formatDate(row.updated_at || row.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tableBodyCellSx}>
                          <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setViewRow(row);
                                  setViewOpen(true);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(st === "pending" || st === "rejected") && (
                              <Tooltip title="Submit to Meta">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!!actionLoadingId}
                                    onClick={() => runAction(row, "submit_meta")}
                                  >
                                    {busy("submit_meta") ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <SubmitIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {(st === "approved" || st === "paused" || st === "pending") && (
                              <Tooltip title="Verify from Meta">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!!actionLoadingId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      runAction(row, "verify_meta");
                                    }}
                                  >
                                    {busy("verify_meta") ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <VerifyIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {st === "approved" && (
                              <Tooltip title="Pause">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!!actionLoadingId}
                                    onClick={() => runAction(row, "update_status", { status: "paused" })}
                                  >
                                    {busy("update_status") ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <PauseIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {st === "paused" && (
                              <Tooltip title="Resume">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!!actionLoadingId}
                                    onClick={() =>
                                      runAction(row, "update_status", { status: "approved" })
                                    }
                                  >
                                    {busy("update_status") ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <ResumeIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {(st === "pending" || st === "rejected") && (
                              <Tooltip title="Remove template">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!!actionLoadingId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const label = row.name || `#${row.id}`;
                                      const metaNote = row.meta_template_id
                                        ? "\n\nNote: This template may already exist on Meta; only the EduOK copy will be removed."
                                        : "";
                                      if (
                                        !window.confirm(
                                          `Remove ${st} template "${label}" for this school?${metaNote}`
                                        )
                                      ) {
                                        return;
                                      }
                                      runAction(row, "delete");
                                    }}
                                  >
                                    {busy("delete") ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={() => {
              setManageSchool(null);
              setManageTemplates([]);
              setManageStatus("all");
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add school template</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Autocomplete
              options={schoolOptions}
              loading={schoolsLoading}
              value={selectedSchool}
              getOptionLabel={(opt) =>
                opt?.school_name ? `${opt.school_name} (${opt.school_code})` : opt?.school_code || ""
              }
              isOptionEqualToValue={(a, b) => a?.school_code === b?.school_code}
              onInputChange={(_, value, reason) => {
                if (reason === "input") loadSchoolOptions(value);
              }}
              onChange={(_, value) => {
                setSelectedSchool(value);
                setForm((p) => ({ ...p, school_code: value?.school_code || "" }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="School *" placeholder="Search connected schoolâ€¦" />
              )}
            />
            <TextField
              label="Template name *"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              helperText="Lowercase letters, numbers, underscores (Meta naming)"
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Language"
                size="small"
                value={form.language}
                onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
                fullWidth
              />
            </Stack>
            <TextField
              label="Group name"
              value={form.group_name}
              onChange={(e) => setForm((p) => ({ ...p, group_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Header (optional)"
              value={form.header_text}
              onChange={(e) => setForm((p) => ({ ...p, header_text: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Body *"
              value={form.body_text}
              onChange={(e) => setForm((p) => ({ ...p, body_text: e.target.value }))}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Footer (optional)"
              value={form.footer_text}
              onChange={(e) => setForm((p) => ({ ...p, footer_text: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.submit_to_meta}
                  onChange={(e) => setForm((p) => ({ ...p, submit_to_meta: e.target.checked }))}
                />
              }
              label="Submit to Meta after save"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: WA,
              "&:hover": { bgcolor: WA_DARK },
            }}
          >
            {saving ? "Savingâ€¦" : "Save template"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verifyReportOpen}
        onClose={() => setVerifyReportOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          Verify from Meta
          {verifyReport?.template_name ? ` · ${verifyReport.template_name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {verifyReport?.error ? (
            <Typography variant="body2" color="error.main" sx={{ mb: 1.5, fontWeight: 650 }}>
              {verifyReport.error}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 650, color: "#475569" }}>
            {verifyReport?.message ||
              (verifyReport?.matched
                ? "Meta and DB match"
                : verifyReport?.synced
                  ? "Synced local template from Meta"
                  : "Comparison complete")}
          </Typography>
          {verifyNeedsStatusUpdate ? (
            <Box
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1.1,
                borderRadius: "10px",
                bgcolor: alpha("#ed6c02", 0.1),
                border: `1px solid ${alpha("#ed6c02", 0.28)}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#e65100" }}>
                Meta is APPROVED but EduOK is still pending. Click Update to set EduOK status to
                approved.
              </Typography>
            </Box>
          ) : null}
          {Array.isArray(verifyReport?.report?.rows) && verifyReport.report.rows.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Field", "Meta", "EduOK", "Match"].map((h) => (
                      <TableCell key={h} sx={{ ...tableHeadCellSx, py: 1, px: 1.25 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verifyReport.report.rows.map((r) => (
                    <TableRow key={r.field} sx={tableRowSx}>
                      <TableCell sx={{ ...tableBodyCellSx, py: 1, px: 1.25, fontWeight: 700 }}>
                        {r.field}
                      </TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, py: 1, px: 1.25, maxWidth: 160 }}>
                        <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
                          {r.meta ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, py: 1, px: 1.25, maxWidth: 160 }}>
                        <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
                          {r.eduok ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, py: 1, px: 1.25 }}>
                        <Chip
                          size="small"
                          label={r.match ? "Yes" : "No"}
                          color={r.match ? "success" : "warning"}
                          sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {verifyReport?.meta?.status
                ? `Meta status: ${verifyReport.meta.status}`
                : "No field comparison rows returned."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: "wrap" }}>
          {verifyNeedsStatusUpdate ? (
            <Button
              variant="contained"
              disableElevation
              disabled={
                !!actionLoadingId ||
                actionLoadingId === `update_status-${verifyReport?.template_id}`
              }
              onClick={handleUpdateStatusFromVerify}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: WA,
                mr: "auto",
                "&:hover": { bgcolor: WA_DARK },
              }}
            >
              {actionLoadingId === `update_status-${verifyReport?.template_id}`
                ? "Updating…"
                : "Update EduOK to Approved"}
            </Button>
          ) : null}
          <Button
            onClick={() => setVerifyReportOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Template details</DialogTitle>
        <DialogContent dividers>
          {viewRow ? (
            <Stack spacing={1.25}>
              <Typography variant="body2">
                <strong>School:</strong> {viewRow.school_name || viewRow.school_code}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {viewRow.name}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {viewRow.category || "â€”"}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {viewRow.status || "â€”"}
              </Typography>
              {viewRow.header_text ? (
                <Typography variant="body2">
                  <strong>Header:</strong> {viewRow.header_text}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                <strong>Body:</strong>
                {"\n"}
                {viewRow.body_text || "â€”"}
              </Typography>
              {viewRow.footer_text ? (
                <Typography variant="body2">
                  <strong>Footer:</strong> {viewRow.footer_text}
                </Typography>
              ) : null}
              {viewRow.rejection_reason ? (
                <Typography variant="body2" color="error.main">
                  <strong>Rejection:</strong> {viewRow.rejection_reason}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
});

export default WhatsAppTemplates;
